import React, { useState, useEffect, useCallback } from 'react';
import { Grid } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import OutlinedInput from '@mui/material/OutlinedInput';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import { useNavigate } from 'react-router-dom';

import api from '../../utils/webcall';
import { transformUnits, getErrorMessage } from '../../utils/utils';
import { DEFAULT_MEASUREMENT_UNIT } from '../../utils/constants';
import UserProfile from '../../components/User';
import AccessController from '../../components/access_controller';
import MeasurementUnitSelect from '../../components/commons/MeasurementUnitSelect';

// Shared by AddPreparation and AddWastage/AddUsage, which only differ in
// their API endpoints, success copy, and whether the "already in inventory"
// column applies.
function InventoryTransactionRow(props) {
    const { item, index, update, options, showAlreadyAddedColumn } = props;
    return (
        <TableRow>
            <TableCell align="center">
                <Autocomplete
                    id={`outlined-adornment-transaction-row-${index}`}
                    fullWidth
                    autoSelect
                    sx={{ minWidth: '200px' }}
                    onChange={(e) => update(index, e.target.innerText, 'item_name')}
                    options={options}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            onChange={(e) => update(index, e.target.value, 'item_name')}
                            slotProps={{
                                input: {
                                    ...params.InputProps,
                                    type: 'search',
                                },
                            }}
                        />
                    )}
                />
            </TableCell>
            <TableCell align="center">
                <OutlinedInput
                    id={`outlined-adornment-transaction-quantity-${index}`}
                    value={item['quantity']}
                    onChange={(e) => update(index, e.target.value, 'quantity')}
                />
            </TableCell>
            <TableCell align="center">
                <MeasurementUnitSelect
                    value={item['measurement_unit']}
                    onChange={(e) => update(index, e.target.value, 'measurement_unit')}
                />
            </TableCell>
            {showAlreadyAddedColumn && (
                <TableCell align="center">
                    <Checkbox
                        disabled
                        checked={item['already_added_in_inventory']}
                        onChange={(e) => update(index, e.target.checked, 'already_added_in_inventory')}
                    />
                </TableCell>
            )}
        </TableRow>
    );
}

function InventoryTransactionTable(props) {
    const { data, update, options, showAlreadyAddedColumn } = props;
    if (data.length === 0) {
        return null;
    }
    return (
        <>
            {data.map((item, index) => (
                <InventoryTransactionRow
                    key={`transaction-row-${index}`}
                    item={item}
                    index={index}
                    update={update}
                    options={options}
                    showAlreadyAddedColumn={showAlreadyAddedColumn}
                />
            ))}
        </>
    );
}

function AddInventoryTransactionForm(props) {
    const {
        showErrorMessage,
        showSuccessMessage,
        title,
        listEndpoint,
        submitEndpoint,
        successMessage,
        navigateTo,
        showAlreadyAddedColumn = false,
    } = props;

    const [inventoryItemsList, setInventoryItemsList] = useState([]);
    const [transactionData, setTransactionData] = useState([]);
    const navigate = useNavigate();
    const username = UserProfile.getName();

    const update = useCallback((index, value, target) => {
        setTransactionData((prev) => {
            const next = [...prev];
            next[index] = { ...next[index], [target]: value };
            return next;
        });
    }, []);

    useEffect(() => {
        api.get(listEndpoint).then((res) => {
            if (res.status === 200) {
                setInventoryItemsList(res.data);
            } else {
                showErrorMessage(getErrorMessage(res));
            }
        }).catch((error) => {
            showErrorMessage(getErrorMessage(error));
        });
    }, [listEndpoint, showErrorMessage]);

    const handleSubmit = () => {
        const payload = transactionData.map((row) => {
            const [quantity, measurement_unit] = transformUnits(row['quantity'], row['measurement_unit']);
            return { ...row, quantity, measurement_unit };
        });

        api.post(submitEndpoint, { payload }).then((res) => {
            if (res.status === 200) {
                showSuccessMessage(successMessage);
                navigate(navigateTo);
            }
        }).catch((error) => {
            showErrorMessage(getErrorMessage(error));
        });
    };

    const addNewRow = () => {
        setTransactionData((prev) => [
            ...prev,
            {
                item_name: '',
                quantity: '',
                measurement_unit: DEFAULT_MEASUREMENT_UNIT,
                ...(showAlreadyAddedColumn ? { already_added_in_inventory: false } : {}),
            },
        ]);
    };

    return (
        <AccessController onlyAdmin={false}>
            <Grid container direction="column" rowSpacing={2} alignItems="center">
                <h2>DIVASH | ADMIN</h2>
                <Grid sx={{ textAlign: 'center' }}>
                    <i>Welcome {username}</i>. <br />
                    <b>{title}</b>
                </Grid>
                <Grid>
                    <TableContainer component={Paper} style={{ overflowX: 'initial' }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell align="center">Item Name</TableCell>
                                    <TableCell align="center">Quantity</TableCell>
                                    <TableCell align="center">Measurement Unit</TableCell>
                                    {showAlreadyAddedColumn && (
                                        <TableCell align="center">Was already included in Inventory</TableCell>
                                    )}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <InventoryTransactionTable
                                    data={transactionData}
                                    update={update}
                                    options={inventoryItemsList}
                                    showAlreadyAddedColumn={showAlreadyAddedColumn}
                                />
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
                <Grid>
                    <Button variant="contained" onClick={addNewRow} sx={{ margin: '10px' }}>
                        Add New Row
                    </Button>
                </Grid>
                <Grid>
                    <Button
                        variant="outlined"
                        sx={{ margin: '10px' }}
                        onClick={(e) => {
                            e.preventDefault();
                            handleSubmit();
                        }}
                        disabled={transactionData.length === 0}
                    >
                        Submit
                    </Button>
                </Grid>
            </Grid>
        </AccessController>
    );
}

export default AddInventoryTransactionForm;
